import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { FaRegUser } from "react-icons/fa";

const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";

const URLInputForm = () => {
	const [currentUrl, setCurrentUrl] = useState("");
	const [urlSet, setUrlSet] = useState(new Set());
	const [error, setError] = useState(null);
	const [scrapedResults, setScrapedResults] = useState(null);
	const [loading, setLoading] = useState(false);
	const [user, setUser] = useState(null);
	const [variant, setVariant] = useState(null);
	const navigate = useNavigate();

	const auth = getAuth();

	const urlPattern = /^(https:\/\/)?(www\.)?(airbnb\.com\/rooms\/\d+(\?.*)?|zillow\.com\/.+)$/;

	useEffect(() => {
		const fetchLayoutVariant = async () => {
			try {
				const response = await axios.get(`${backendUrl}/api/get-layout`);
				setVariant(response.data.variant);
			} catch (error) {
				console.error("Error fetching layout variant:", error);
				setError("Failed to fetch layout variant. Using default layout.");
			}
		};

		// Safe way to handle Firebase auth state
		let unsubscribe = () => {};
		try {
			unsubscribe =
				onAuthStateChanged(auth, (currentUser) => {
					setUser(currentUser);
				}) || (() => {});
		} catch (error) {
			console.error("Error setting up auth state listener:", error);
		}

		fetchLayoutVariant();

		return () => {
			try {
				unsubscribe();
			} catch (error) {
				console.error("Error unsubscribing from auth state:", error);
			}
		};
	}, []);

	const handleUrlChange = (event) => {
		setCurrentUrl(event.target.value);
	};

	const handleAddUrl = () => {
		const trimmedUrl = currentUrl.trim();

		if (trimmedUrl === "") {
			setError("Please enter a valid non-empty URL.");
		} else if (!urlPattern.test(trimmedUrl)) {
			setError("Please enter a valid Airbnb or Zillow URL.");
		} else if (urlSet.has(trimmedUrl)) {
			setError("Oops! It looks like you already entered that URL! :)");
		} else {
			setUrlSet((prevSet) => new Set(prevSet).add(trimmedUrl));
			setCurrentUrl("");
			setError(null);
		}
	};

	const handleKeyPress = (event) => {
		if (event.key === "Enter") {
			handleAddUrl();
		}
	};

	const handleClearUrls = () => {
		setUrlSet(new Set());
		setError(null);
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (urlSet.size === 0) {
			setError("No URLs to submit. Please add a URL first.");
			return;
		}

		setLoading(true);
		setError(null);

		try {
			console.log("Backend URL:", backendUrl);
			const urlList = Array.from(urlSet);
			const response = await axios.post(`${backendUrl}/api/submit-urls`, {
				urls: urlList,
			});

			// Check if we actually got meaningful results
			if (response.data.results && response.data.results.length > 0) {
				alert("URLs analyzed successfully! Click view results to see the properties.");
				setScrapedResults(response.data.results);
				setUrlSet(new Set());
				setError(null);
			} else {
				alert("URLs analysis was unsuccessful. No property data could be retrieved.");
				setError("No properties were scraped. The scraper services may be offline.");
			}
		} catch (error) {
			console.error("Error submitting URLs:", error);
			setError("Failed to submit URLs. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const handleRestart = () => {
		window.location.reload();
	};

	const goToResults = () => {
		if (scrapedResults) {
			navigate("/results", { state: { results: scrapedResults } });
		} else {
			setError("No results to display. Please submit URLs first.");
		}
	};

	const shortenUrl = (url) => {
		try {
			const parsedUrl = new URL(url);
			const domain = parsedUrl.hostname.replace("www.", "");
			const pathParts = parsedUrl.pathname.split("/").filter(Boolean);

			if (domain.includes("airbnb.com") && pathParts[0] === "rooms") {
				return `${domain}/${pathParts[0]}/${pathParts[1]}`;
			}

			if (domain.includes("zillow.com") && pathParts[0] === "homedetails" && pathParts.length > 1) {
				const propertyId = pathParts[1].split("_")[0];
				return `${domain}/property/${propertyId}`;
			}

			return `${domain}/${pathParts.slice(0, 2).join("/")}`;
		} catch {
			return url;
		}
	};

	return (
		<div className="flex flex-col items-center justify-center w-3/4 p-6 mx-auto mt-8 text-center bg-white rounded-lg shadow-lg md:w-1/2 lg:w-1/3">
			<div className="flex justify-between w-full mb-6">
				<div className="flex items-center">
					{user ? (
						<>
							<img
								src={user.photoURL || "https://via.placeholder.com/40"}
								alt="User Profile"
								className="w-10 h-10 rounded-full mr-2"
							/>
							<span className="text-lg font-semibold">{user.displayName || "User"}</span>
						</>
					) : (
						<FaRegUser className="text-2xl" />
					)}
				</div>
			</div>
			<h2 className="mb-6 text-3xl font-bold text-gray-800">URL Submission</h2>
			<h3 className="mb-4 text-xl italic text-gray-600">Enter a valid Airbnb or Zillow URL below.</h3>

			<div className={`flex ${variant === "stacked" ? "flex-col" : "flex-row"} w-full mb-4`}>
				<input
					type="text"
					value={currentUrl}
					onChange={handleUrlChange}
					onKeyDown={handleKeyPress}
					placeholder="Enter a URL"
					className="flex-grow p-3 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
				/>
				<button
					type="button"
					onClick={handleAddUrl}
					className="px-4 py-3 font-medium text-white rounded-lg bg-gradient-to-r from-purple-500 to-blue-600 hover:from-blue-600 hover:to-purple-500"
				>
					Add URL
				</button>
			</div>

			{error && <p className="mb-4 text-red-500">{error}</p>}

			<ul className="w-full mb-6 text-left">
				{Array.from(urlSet).map((url, index) => (
					<li key={index} className="p-2 mb-2 text-gray-800 bg-gray-100 rounded-lg" title={url}>
						{shortenUrl(url)}
					</li>
				))}
			</ul>

			{scrapedResults && (
				<button
					type="button"
					onClick={goToResults}
					className="w-full px-6 py-3 mt-4 font-semibold text-white rounded-lg bg-purple-600 hover:bg-purple-700"
				>
					View Results
				</button>
			)}
			{!scrapedResults && (
				<button
					type="button"
					onClick={handleClearUrls}
					className="w-full px-6 py-3 mt-4 font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700"
				>
					Clear URLs
				</button>
			)}
			<button
				type="button"
				onClick={scrapedResults ? handleRestart : handleSubmit}
				className={`w-full px-6 py-3 mt-4 font-semibold text-white rounded-lg ${
					scrapedResults ? "bg-blue-600 hover:bg-blue-700" : "bg-green-600 hover:bg-green-700"
				}`}
				disabled={loading}
			>
				{loading ? "Analyzing..." : scrapedResults ? "Restart" : "Analyze URLs"}
			</button>
		</div>
	);
};

export default URLInputForm;
